import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Outlet, useRouterState } from '@tanstack/react-router';
import { useLayoutEffect } from 'react';
import { aplicarNivel, mapaRotaNivel } from './nivel/mapaRotaNivel';
import { PaginaRotina } from './paginas/PaginaRotina';

// Núcleo no chunk inicial (shell, lista); formulário, detalhe e diagnóstico em chunks próprios (07 §6.2 item 1).
const PaginaModulo = lazyRouteComponent(() => import('./paginas/PaginaModulo'), 'PaginaModulo');
const PaginaFormulario = lazyRouteComponent(() => import('./paginas/PaginaFormulario'), 'PaginaFormulario');
const PaginaDetalhe = lazyRouteComponent(() => import('./paginas/PaginaDetalhe'), 'PaginaDetalhe');
const PaginaDiagnostico = lazyRouteComponent(() => import('./paginas/PaginaDiagnostico'), 'PaginaDiagnostico');
const PaginaConta = lazyRouteComponent(() => import('./conta/PaginaConta'), 'PaginaConta');
import { Entrar } from '../shell/Entrar';
import { Shell } from '../shell/Shell';
import { TelaInicial } from '../shell/TelaInicial';

function Raiz() {
  const caminho = useRouterState({ select: (s) => s.location.pathname });
  // O nível segue a rota (07 §0.1); dentro de /r/ a página refina com o padrão do esquema.
  useLayoutEffect(() => aplicarNivel(mapaRotaNivel(caminho)), [caminho]);
  return <Outlet />;
}

export interface BuscaLista {
  q?: string;
  o?: string;
  destacar?: number;
}

const raiz = createRootRoute({ component: Raiz });
const entrar = createRoute({ getParentRoute: () => raiz, path: '/entrar', component: Entrar, validateSearch: (s: Record<string, unknown>) => ({ volta: typeof s.volta === 'string' ? s.volta : undefined }) });
const app = createRoute({ getParentRoute: () => raiz, id: 'app', component: Shell });
const inicio = createRoute({ getParentRoute: () => app, path: '/', component: TelaInicial });
const modulo = createRoute({ getParentRoute: () => app, path: '/m/$modulo', component: PaginaModulo });
const diagnostico = createRoute({ getParentRoute: () => app, path: '/diagnostico', component: () => <PaginaDiagnostico /> });
const conta = createRoute({ getParentRoute: () => app, path: '/conta', component: PaginaConta });
export const rotaRotina = createRoute({ getParentRoute: () => app, path: '/r/$rotina', component: PaginaRotina });
export const rotaLista = createRoute({
  getParentRoute: () => rotaRotina,
  path: '/',
  // A lista é desenhada pela PaginaRotina (fica atrás da janela do registro); a rota só valida a busca.
  component: () => null,
  validateSearch: (s: Record<string, unknown>): BuscaLista => ({
    q: typeof s.q === 'string' && s.q ? s.q : undefined,
    o: typeof s.o === 'string' && s.o ? s.o : undefined,
    destacar: s.destacar === undefined ? undefined : Number(s.destacar) || undefined,
  }),
});
const novo = createRoute({ getParentRoute: () => rotaRotina, path: '/novo', component: () => <PaginaFormulario modo="incluir" /> });
const rotinaDiagnostico = createRoute({ getParentRoute: () => rotaRotina, path: '/diagnostico', component: () => <PaginaDiagnostico daRotina /> });
export const rotaRegistro = createRoute({ getParentRoute: () => rotaRotina, path: '/$id', component: PaginaDetalhe });
const editar = createRoute({ getParentRoute: () => rotaRotina, path: '/$id/editar', component: () => <PaginaFormulario modo="alterar" /> });
const copiar = createRoute({ getParentRoute: () => rotaRotina, path: '/$id/copiar', component: () => <PaginaFormulario modo="copiar" /> });

const arvore = raiz.addChildren([
  entrar,
  app.addChildren([inicio, modulo, diagnostico, conta, rotaRotina.addChildren([rotaLista, novo, rotinaDiagnostico, rotaRegistro, editar, copiar])]),
]);

export const roteador = createRouter({ routeTree: arvore, defaultPreload: 'intent', defaultPreloadDelay: 80, scrollRestoration: true });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof roteador;
  }
}
